import { i18n } from '@lingui/core'

import { MONEY_PATTERN } from './formatPoints'

/**
 * Renders a 2-decimal money string as a whole number, grouped for the active
 * locale: `"124050.99"` → `124,050`. The fraction is dropped (truncation toward
 * zero), and a negative value whose integer part is zero (`"-0.50"`) renders as
 * `0`, never `-0`.
 *
 * The value never becomes a float — no `Number`, `parseFloat`, `Math.trunc` or
 * `toFixed`. The integer digits are grouped as a `bigint`, exactly as
 * `formatPoints` does, so a figure beyond `Number.MAX_SAFE_INTEGER` keeps every
 * digit.
 *
 * Total by construction, for the same reason as `formatPoints`: a string that is
 * not 2-decimal money (`MONEY_PATTERN`) comes back unchanged. This util is on the
 * render path and the app has no ErrorBoundary, so a throw here would blank the
 * dashboard; validation belongs one layer up, in the state derivation.
 */
export function formatWholePoints(value: string): string {
  if (typeof value !== 'string' || !MONEY_PATTERN.test(value)) {
    return value
  }

  const isNegative = value.startsWith('-')
  const [integerPart] = (isNegative ? value.slice(1) : value).split('.')

  const formatted = new Intl.NumberFormat(i18n.locale || undefined, {
    maximumFractionDigits: 0
  }).format(BigInt(integerPart))

  const isZero = /^0+$/.test(integerPart)

  return isNegative && !isZero ? `-${formatted}` : formatted
}
